import { v4 as uuidv4 } from 'uuid';
import type { LogLevel, Environment, ClientLogEntry, ServerLogEntry, LambdaLogEntry, LogEntry } from './types';

type LambdaContext = {
  getRemainingTimeInMillis?: () => number;
  invokedFunctionArn?: string;
  awsRequestId?: string;
};

/**
 * 環境を自動検出
 */
function detectEnvironment(): Environment {
  if (typeof window !== 'undefined') {
    return 'client';
  } else if (process.env.AWS_LAMBDA_FUNCTION_NAME) {
    return 'lambda';
  } else {
    return 'server';
  }
}

/**
 * Sentry へ転送してよい診断 context のキー（許可リスト）。
 * metadata を丸ごと送ると本文・トークン・メール等が混入し得るため、ここに無いキーは送らない。
 * 値はプリミティブのみ受け付ける。
 */
// 同時に呼ばれても同じ読込を待つよう共有する（送信済み判定を import 待ちの後で揃えるため）
let sentryModule: Promise<typeof import('@sentry/nextjs')> | undefined;

const SENTRY_DIAGNOSTIC_KEYS = [
  'operation',
  'apiPath',
  'method',
  'durationMs',
  'failurePhase',
  'status',
  'errorKind',
  'errorCode',
  'online',
  'visibilityState',
  'clientRequestId',
] as const;

function pickSentryDiagnostics(context?: Partial<LogEntry>): Record<string, string | number | boolean> {
  const picked: Record<string, string | number | boolean> = {};
  const metadata = context?.metadata;
  if (!metadata) {
    return picked;
  }
  for (const key of SENTRY_DIAGNOSTIC_KEYS) {
    const value = metadata[key];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      picked[key] = value;
    }
  }
  return picked;
}

/**
 * 型安全な構造化ロガー
 * クライアント、サーバー、Lambda環境で統一されたログフォーマットを強制
 */
export class StructuredLogger {
  private readonly environment: Environment;
  private readonly serviceName: string;
  private readonly enableConsoleLog: boolean;
  private readonly sentryReportedErrors = new WeakSet<Error>();

  constructor(options?: {
    environment?: Environment;
    serviceName?: string;
    enableConsoleLog?: boolean;
  }) {
    this.environment = options?.environment || detectEnvironment();
    this.serviceName = options?.serviceName || process.env.SERVICE_NAME || `kakusill-${this.environment}`;
    this.enableConsoleLog = options?.enableConsoleLog ?? process.env.ENABLE_CONSOLE_LOG !== 'false';
  }

  /**
   * ログエントリの作成（環境に応じた型を返す）
   */
  private createLogEntry(level: LogLevel, message: string, context?: Partial<LogEntry>): LogEntry {
    const baseEntry = {
      timestamp: new Date().toISOString(),
      level,
      environment: this.environment,
      service: this.serviceName,
      message,
      traceId: context?.traceId || this.generateTraceId(),
      spanId: context?.spanId || uuidv4(),
      ...context,
    };

    // 環境別の詳細情報を追加
    switch (this.environment) {
      case 'client':
        return this.createClientLogEntry(baseEntry);
      case 'server':
        return this.createServerLogEntry(baseEntry);
      case 'lambda':
        return this.createLambdaLogEntry(baseEntry);
    }
  }

  /**
   * クライアント環境のログエントリ作成
   */
  private createClientLogEntry(base: Partial<LogEntry>): ClientLogEntry {
    const entry: ClientLogEntry = {
      ...base,
      timestamp: (base.timestamp as string) || new Date().toISOString(),
      level: (base.level as LogLevel) || 'INFO',
      service: (base.service as string) || this.serviceName,
      message: (base.message as string) || '',
      environment: 'client',
      client: {
        url: window.location.href,
        referrer: document.referrer,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
      },
      runtime: {
        browser: {
          name: this.getBrowserName(),
          version: this.getBrowserVersion(),
          userAgent: navigator.userAgent,
        },
      },
    };
    return entry;
  }

  /**
   * サーバー環境のログエントリ作成
   */
  private createServerLogEntry(base: Partial<LogEntry>): ServerLogEntry {
    const entry: ServerLogEntry = {
      ...base,
      timestamp: (base.timestamp as string) || new Date().toISOString(),
      level: (base.level as LogLevel) || 'INFO',
      service: (base.service as string) || this.serviceName,
      message: (base.message as string) || '',
      environment: 'server',
      server: {
        hostname: process.env.HOSTNAME || 'unknown',
        pid: process.pid,
        memory: {
          rss: process.memoryUsage().rss / 1024 / 1024, // MB
          heapTotal: process.memoryUsage().heapTotal / 1024 / 1024,
          heapUsed: process.memoryUsage().heapUsed / 1024 / 1024,
        },
      },
      runtime: {
        node: {
          version: process.version,
          platform: process.platform,
          arch: process.arch,
        },
      },
    };
    return entry;
  }

  /**
   * Lambda環境のログエントリ作成
   */
  private createLambdaLogEntry(base: Partial<LogEntry>): LambdaLogEntry {
    const context = this.getLambdaContext();
    const lambdaColdStart = Boolean((global as { lambdaColdStart?: boolean }).lambdaColdStart);
    const timeRemaining = this.getLambdaTimeRemaining(context);
    const invokedFunctionArn = context?.invokedFunctionArn ?? '';
    const requestId = context?.awsRequestId ?? '';
    const functionName = process.env.AWS_LAMBDA_FUNCTION_NAME ?? '';
    const functionVersion = process.env.AWS_LAMBDA_FUNCTION_VERSION ?? '';
    const memoryLimit = process.env.AWS_LAMBDA_FUNCTION_MEMORY_SIZE ?? '';
    const region = process.env.AWS_REGION ?? '';
    const entry: LambdaLogEntry = {
      ...base,
      timestamp: (base.timestamp as string) || new Date().toISOString(),
      level: (base.level as LogLevel) || 'INFO',
      service: (base.service as string) || this.serviceName,
      message: (base.message as string) || '',
      environment: 'lambda',
      lambda: {
        coldStart: lambdaColdStart,
        timeRemaining,
        invokedFunctionArn,
      },
      runtime: {
        lambda: {
          functionName,
          functionVersion,
          requestId,
          memoryLimit,
          region,
        },
      },
    };
    return entry;
  }

  private getLambdaContext(): LambdaContext | undefined {
    return (global as { lambdaContext?: LambdaContext }).lambdaContext;
  }

  private getLambdaTimeRemaining(context: LambdaContext | undefined): number {
    if (typeof context?.getRemainingTimeInMillis !== 'function') {
      return 0;
    }
    return context.getRemainingTimeInMillis();
  }

  /**
   * 循環参照を含む値も安全に JSON 文字列化する。
   * 障害時のロギング経路で例外を投げないことが目的。
   */
  private safeStringify(value: unknown): string {
    const seen = new WeakSet<object>();
    const replacer = (_key: string, val: unknown): unknown => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val as object)) {
          return '[Circular]';
        }
        seen.add(val as object);
      }
      if (typeof val === 'bigint') {
        return val.toString();
      }
      return val;
    };

    try {
      return JSON.stringify(value, replacer, 2);
    } catch {
      return JSON.stringify(
        { serializationError: 'Circular reference or non-serializable value' },
        null,
        2,
      );
    }
  }

  /**
   * ログ出力
   */
  private async log(level: LogLevel, message: string, context?: Partial<LogEntry>): Promise<void> {
    const entry = this.createLogEntry(level, message, context);

    // コンソール出力
    if (this.enableConsoleLog && process.env.NODE_ENV !== 'test') {
      const consoleMethod = this.getConsoleMethod(level);
      const parts: string[] = [];
      if (entry.error) {
        parts.push(this.safeStringify(entry.error));
      }
      if (entry.metadata) {
        parts.push(this.safeStringify(entry.metadata));
      }
      const suffix = parts.length > 0 ? '\n' + parts.join('\n') : '';
      consoleMethod(`[${level}] ${message}${suffix}`);
    }
  }

  // パブリックメソッド
  debug(message: string, context?: Partial<LogEntry>) {
    return this.log('DEBUG', message, context);
  }

  info(message: string, context?: Partial<LogEntry>) {
    return this.log('INFO', message, context);
  }

  /**
   * `error` / `fatal` と同じ並びで Error を受け取る。warn で握り潰した例外の
   * 詳細が構造化ログへ載らないと、本番で「何かに失敗した」しか分からなくなる。
   *
   * Sentry へは送らない。warn は続行できた事象を記録するレベルで、
   * 通知の対象は `error` / `fatal` に限る。
   */
  warn(message: string, error?: Error | unknown, context?: Partial<LogEntry>) {
    return this.log('WARN', message, this.mergeErrorInfo(context, error));
  }

  /**
   * `Error.cause` を再帰的に正規化する。
   * Error の標準プロパティ（name/message/stack）は non-enumerable のため、
   * `JSON.stringify` にそのまま渡すと `{}` になり詳細が失われる。
   * 深さは循環/暴走防止のため最大 3 段までに制限する。
   */
  private normalizeErrorCause(value: unknown, depth = 0): unknown {
    if (!(value instanceof Error)) {
      return value;
    }

    if (depth >= 3) {
      return '[Truncated error cause chain]';
    }

    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      code: (value as { code?: string }).code,
      cause: this.normalizeErrorCause((value as { cause?: unknown }).cause, depth + 1),
    };
  }

  /**
   * Error / 非 Error / undefined を統一した LogEntry 部分形に変換する。
   * `error()` と `fatal()` で共通利用するためのヘルパー。
   */
  private buildErrorInfo(error?: Error | unknown): Partial<LogEntry> {
    if (error instanceof Error) {
      return {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          code: (error as { code?: string }).code,
          cause: this.normalizeErrorCause((error as { cause?: unknown }).cause),
        },
      };
    }
    return error ? { metadata: { error } } : {};
  }

  /**
   * context と Error 情報を混ぜる。
   *
   * `{ ...context, ...errorInfo }` と素で広げると、非 Error 値を渡したときの
   * `errorInfo.metadata` が `context.metadata` を丸ごと置き換えてしまう。
   * `LogEntry.metadata` は 1 つの Record なので、metadata だけは個別に混ぜる。
   */
  private mergeErrorInfo(context: Partial<LogEntry> | undefined, error?: Error | unknown): Partial<LogEntry> {
    const errorInfo = this.buildErrorInfo(error);
    const hasMetadata = context?.metadata !== undefined || errorInfo.metadata !== undefined;

    return {
      ...context,
      ...errorInfo,
      ...(hasMetadata ? { metadata: { ...context?.metadata, ...errorInfo.metadata } } : {}),
    };
  }

  private async captureToSentry(message: string, error?: Error | unknown, context?: Partial<LogEntry>): Promise<void> {
    if (this.environment === 'lambda' || !(error instanceof Error) || this.sentryReportedErrors.has(error)) {
      return;
    }

    try {
      const { captureException, getClient } = await (sentryModule ??= import('@sentry/nextjs'));
      // import 待ちの間に同じ Error の別呼び出しが送信済みの場合があるため、送信直前に再確認する
      if (!getClient() || this.sentryReportedErrors.has(error)) {
        return;
      }
      this.sentryReportedErrors.add(error);
      captureException(error, { extra: { logMessage: message, ...pickSentryDiagnostics(context) } });
    } catch {
      // 読込失敗を保持し続けないよう、次回の呼び出しで読み直す
      sentryModule = undefined;
      // ロギング経路では Sentry 側の失敗を外へ投げない。
    }
  }

  error(message: string, error?: Error | unknown, context?: Partial<LogEntry>) {
    void this.captureToSentry(message, error, context);
    return this.log('ERROR', message, this.mergeErrorInfo(context, error));
  }

  fatal(message: string, error?: Error | unknown, context?: Partial<LogEntry>) {
    void this.captureToSentry(message, error, context);
    return this.log('FATAL', message, this.mergeErrorInfo(context, error));
  }

  /**
   * トレースIDの生成
   */
  private generateTraceId(): string {
    return uuidv4();
  }

  /**
   * ブラウザ名の取得
   */
  private getBrowserName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  /**
   * ブラウザバージョンの取得
   */
  private getBrowserVersion(): string {
    const ua = navigator.userAgent;
    const match = ua.match(/(Chrome|Firefox|Safari|Edge)\/(\d+)/);
    return match ? match[2] : 'Unknown';
  }

  /**
   * コンソールメソッドの取得
   */
  private getConsoleMethod(level: LogLevel): (...args: unknown[]) => void {
    switch (level) {
      case 'DEBUG':
        return console.log;
      case 'INFO':
        return console.info;
      case 'WARN':
        return console.warn;
      case 'ERROR':
      case 'FATAL':
        return console.error;
      default:
        return console.log;
    }
  }
}

// シングルトンインスタンス
let loggerInstance: StructuredLogger | null = null;

export function getLogger(): StructuredLogger {
  if (!loggerInstance) {
    loggerInstance = new StructuredLogger();
  }
  return loggerInstance;
}
